/* ============================================================
   RYVOX — collection.js
   Collection + search enhancements:
   - AJAX facet filtering via the Section Rendering API
   - Mobile catalog accordion <-> desktop persistent tree sync
   - Quick-add support for AJAX-swapped product cards
   - Predictive search enrichment (price + stock badge)
   Loaded with defer from main-collection / main-search sections.
   ============================================================ */
(function () {
  'use strict';

  if (window.__ryvoxCollectionLoaded) return;
  window.__ryvoxCollectionLoaded = true;

  var routes = window.RyvoxRoutes || {};
  var strings = window.RyvoxStrings || {};
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function money(cents) {
    try {
      return (window.Shopify && Shopify.formatMoney)
        ? Shopify.formatMoney(cents, window.RyvoxMoneyFormat)
        : '$' + (cents / 100).toFixed(2);
    } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ============================================================
     Catalog accordion: open tree on desktop, collapsed on mobile
     ============================================================ */
  var catalogAcc = $('[data-catalog-acc]');
  var desktopMq = window.matchMedia('(min-width: 990px)');
  if (catalogAcc) {
    var syncCatalog = function (matches) { catalogAcc.open = !!matches; };
    syncCatalog(desktopMq.matches);
    if (desktopMq.addEventListener) {
      desktopMq.addEventListener('change', function (e) { syncCatalog(e.matches); });
    } else if (desktopMq.addListener) {
      desktopMq.addListener(function (e) { syncCatalog(e.matches); });
    }
  }

  /* ============================================================
     AJAX filtering (Section Rendering API)
     ============================================================ */
  var root = $('[data-collection-root]');
  if (root) {
    var sectionId = root.getAttribute('data-section-id');
    var resultsEl = $('[data-collection-results]', root);
    var facetsForm = $('[data-facets-form]', root);
    var didPush = false;
    var priceTimer = null;
    var inflight = null;

    var buildFilterUrl = function () {
      var params = new URLSearchParams();
      if (facetsForm) {
        new FormData(facetsForm).forEach(function (value, key) {
          if (String(value).trim().length) params.append(key, value);
        });
      }
      var sortSelect = $('[data-sort-select]', root);
      if (sortSelect && sortSelect.value) params.set('sort_by', sortSelect.value);
      var qs = params.toString();
      // location.pathname keeps any /collections/<handle>/<tag> scope segment,
      // so tag-scoped category nav survives facet + sort changes.
      return window.location.pathname + (qs ? '?' + qs : '');
    };

    var renderFromUrl = function (url, push) {
      if (!resultsEl || !sectionId) { window.location.href = url; return; }
      if (inflight) inflight.abort();
      var controller = ('AbortController' in window) ? new AbortController() : null;
      inflight = controller;

      // Links inside section-rendered HTML can carry section_id — keep it out
      // of the address bar and rebuild it cleanly for the fetch.
      var cleanUrl = new URL(url, window.location.origin);
      cleanUrl.searchParams.delete('section_id');
      url = cleanUrl.pathname + cleanUrl.search;

      var fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);
      resultsEl.setAttribute('aria-busy', 'true');

      fetch(fetchUrl.toString(), controller ? { signal: controller.signal } : {})
        .then(function (r) {
          if (!r.ok) throw new Error('Section render failed');
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');

          var newResults = $('[data-collection-results]', doc);
          if (newResults) resultsEl.innerHTML = newResults.innerHTML;

          var curFacets = $('[data-facets-content]', root);
          var newFacets = $('[data-facets-content]', doc);
          if (curFacets && newFacets) curFacets.innerHTML = newFacets.innerHTML;

          var curToggle = $('[data-filter-toggle]', root);
          var newToggle = $('[data-filter-toggle]', doc);
          if (curToggle && newToggle) curToggle.innerHTML = newToggle.innerHTML;

          var curCount = $('[data-results-count]');
          var newCount = $('[data-results-count]', doc);
          if (curCount && newCount) curCount.textContent = newCount.textContent;

          // Tag-scoped category nav (bike collections): re-sync active highlight.
          var curCatNav = $('[data-catnav]', root);
          var newCatNav = $('[data-catnav]', doc);
          if (curCatNav && newCatNav) curCatNav.innerHTML = newCatNav.innerHTML;

          // Swapped-in cards would otherwise stay hidden by the reveal animation.
          $all('.reveal', resultsEl).forEach(function (el) { el.classList.add('is-in'); });

          resultsEl.setAttribute('aria-busy', 'false');
          if (push) {
            history.pushState({ ryvoxCollection: true }, '', url);
            didPush = true;
          }
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          resultsEl.setAttribute('aria-busy', 'false');
          window.location.href = url; // graceful fallback: full navigation
        });
    };

    var applyFilters = function () { renderFromUrl(buildFilterUrl(), true); };

    // Facet checkboxes, price inputs, sort select — delegated (survives swaps)
    document.addEventListener('change', function (e) {
      if (!root.contains(e.target)) return;
      if (e.target.matches('[data-sort-select]')) { applyFilters(); return; }
      if (facetsForm && facetsForm.contains(e.target)) {
        if (e.target.type === 'number') {
          clearTimeout(priceTimer);
          priceTimer = setTimeout(applyFilters, 400);
        } else {
          applyFilters();
        }
      }
    });

    if (facetsForm) {
      facetsForm.addEventListener('submit', function (e) {
        e.preventDefault();
        applyFilters();
      });
    }

    // Active-filter chips, "clear all" and pagination links — AJAX too
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-facet-link], .pagination a');
      if (!link || !root.contains(link)) return;
      var href = link.getAttribute('href');
      if (!href) return;
      e.preventDefault();
      renderFromUrl(href, true);
      if (link.closest('.pagination')) {
        root.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      }
    });

    window.addEventListener('popstate', function () {
      if (!didPush) return;
      renderFromUrl(window.location.pathname + window.location.search, false);
    });
  }

  /* ============================================================
     Quick-add for AJAX-swapped cards
     global.js binds add-to-cart per form at load; cards injected by
     the section swap need this delegated fallback. It defers to
     global.js on forms that are already handled (defaultPrevented).
     ============================================================ */
  function renderCartDrawerItems(cart) {
    var drawer = $('[data-drawer="cart"]');
    if (!drawer) return;
    var body = $('[data-cart-items]', drawer);
    var subtotal = $('[data-cart-subtotal]', drawer);
    var foot = $('[data-cart-foot]', drawer);
    var empty = $('[data-cart-empty]', drawer);

    $all('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      if (cart.item_count > 0) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = '';
      if (empty) empty.hidden = false;
      if (foot) foot.hidden = true;
      return;
    }
    if (empty) empty.hidden = true;
    if (foot) foot.hidden = false;

    body.innerHTML = cart.items.map(function (item) {
      var img = item.image ? item.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_120x$1') : '';
      var variant = (item.options_with_values || [])
        .filter(function (o) { return o.value !== 'Default Title'; })
        .map(function (o) { return escapeHtml(o.value); }).join(' / ');
      return '' +
        '<div class="line-item" data-line-key="' + escapeHtml(item.key) + '">' +
          '<div class="line-item__img">' + (img ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(item.product_title) + '" width="64" height="64" loading="lazy">' : '') + '</div>' +
          '<div>' +
            '<div class="line-item__title"><a href="' + escapeHtml(item.url) + '">' + escapeHtml(item.product_title) + '</a></div>' +
            (variant ? '<div class="line-item__meta">' + variant + '</div>' : '') +
            '<div class="qty" style="margin-top:.5rem">' +
              '<button type="button" aria-label="Decrease" data-qty-change="' + escapeHtml(item.key) + '" data-qty="' + (item.quantity - 1) + '">&minus;</button>' +
              '<input type="text" value="' + item.quantity + '" readonly aria-label="Quantity">' +
              '<button type="button" aria-label="Increase" data-qty-change="' + escapeHtml(item.key) + '" data-qty="' + (item.quantity + 1) + '">+</button>' +
            '</div>' +
            '<button type="button" class="line-item__remove" data-qty-change="' + escapeHtml(item.key) + '" data-qty="0">' + escapeHtml(strings.remove || 'Remove') + '</button>' +
          '</div>' +
          '<div class="line-item__price">' + money(item.final_line_price) + '</div>' +
        '</div>';
    }).join('');

    if (subtotal) subtotal.textContent = money(cart.total_price);
  }

  function openCartDrawer() {
    var drawer = $('[data-drawer="cart"]');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('form[data-quick-add]')) return;
    if (e.defaultPrevented) return; // global.js already handled this form
    e.preventDefault();
    // aria-disabled does not block a click — without this a double-tap on a
    // card's quick-add POSTs /cart/add.js twice.
    if (form.__ryvoxAdding) return;
    form.__ryvoxAdding = true;

    var btn = form.querySelector('[type="submit"]');
    var original = btn ? btn.innerHTML : '';
    if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.innerHTML = strings.adding || 'Adding…'; }

    fetch(routes.cart_add_url ? routes.cart_add_url + '.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: new FormData(form)
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) {
          if (btn) btn.innerHTML = escapeHtml(res.data.description || strings.error || 'Error');
          return;
        }
        if (btn) btn.innerHTML = strings.added || 'Added ✓';
        return fetch(routes.cart_url ? routes.cart_url + '.js' : '/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            renderCartDrawerItems(cart);
            openCartDrawer();
          });
      })
      .catch(function () { if (btn) btn.innerHTML = strings.error || 'Error'; })
      .finally(function () {
        form.__ryvoxAdding = false;
        setTimeout(function () {
          if (btn) { btn.removeAttribute('aria-disabled'); btn.innerHTML = original; }
        }, 1400);
      });
  });

  /* ============================================================
     Predictive search enrichment: price + stock badge
     global.js renders the base list; this decorates each product
     row from the (HTTP-cached) suggest JSON.
     ============================================================ */
  var predResults = $('[data-predictive-results]');
  var predInput = $('[data-predictive-input]');
  if (predResults && predInput && 'MutationObserver' in window) {
    var enrich = function () {
      var anchors = $all('a.predictive__product[href*="/products/"]:not([data-enriched])', predResults);
      if (!anchors.length) return;
      var q = predInput.value.trim();
      if (q.length < 2) return;
      var url = (routes.predictive_search_url || '/search/suggest') +
        '.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,page&resources[limit]=6';
      fetch(url, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          var byPath = {};
          products.forEach(function (p) { byPath[String(p.url || '').split('?')[0]] = p; });
          anchors.forEach(function (a) {
            a.setAttribute('data-enriched', 'true');
            var p = byPath[a.getAttribute('href').split('?')[0]];
            if (!p || a.querySelector('.predictive__meta')) return;

            var priceCents = Math.round(parseFloat(p.price) * 100);
            var compareCents = Math.round(parseFloat(p.compare_at_price_max || 0) * 100);
            var priceHtml = isNaN(priceCents) ? '' :
              (compareCents > priceCents
                ? '<span class="price__sale">' + money(priceCents) + '</span><span class="price__compare">' + money(compareCents) + '</span>'
                : money(priceCents));

            var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
            var badgeClass, badgeText;
            if (p.available === false) { badgeClass = 'badge--soldout'; badgeText = strings.sold_out || 'Sold out'; }
            /* 18 Oct 2026 QA fix #4: "Reserve" is for deposit bikes only (today the
               RERODE RX, the one bike carrying the pre-order tag). Factory-direct bikes
               carry the older preorder tag but are normal purchases, so here they read
               the way their bike cards do. Parts keep their wording. */
            else if (String(p.type || '').toLowerCase() === 'complete bikes' && tags.indexOf('preorder') !== -1 && tags.indexOf('pre-order') === -1) { badgeClass = 'badge--preorder'; badgeText = 'Ships from the factory'; }
            else if (tags.indexOf('preorder') !== -1 || tags.indexOf('pre-order') !== -1) { badgeClass = 'badge--preorder'; badgeText = 'Reserve'; }
            else if (tags.indexOf('oem-inquiry') !== -1) { badgeClass = 'badge--oem'; badgeText = 'OEM order'; }
            else { badgeClass = 'badge--stock'; badgeText = 'In stock'; }

            var meta = document.createElement('span');
            meta.className = 'predictive__meta';
            meta.innerHTML = (priceHtml ? '<span class="predictive__price">' + priceHtml + '</span>' : '') +
              '<span class="badge ' + badgeClass + '">' + escapeHtml(badgeText) + '</span>';
            a.appendChild(meta);
          });
        })
        .catch(function () {});
    };
    new MutationObserver(enrich).observe(predResults, { childList: true, subtree: true });
    enrich();
  }
})();
